// src/rms/agreements/render.js
// ============================================================================
// Contract text into blocks a page or a PDF can lay out
// ============================================================================
// Deliberately the smallest thing that could work: headings, paragraphs and
// bulleted lists. No bold, no tables, no links, no HTML.
//
// That is a decision, not a shortcut. A contract is read for what it says, and
// every feature added here is a way for two renderers -- the read-only page in
// the browser and the PDF built in the edge function -- to disagree about the
// same stored text. They already share the hardest constraint: tokens are
// resolved once, at send time, and frozen into body_snapshot, so neither
// renderer substitutes anything. Keeping the grammar this small is what keeps
// the rest of the gap closed.
//
// It also means an operator can write their agreement in a plain textarea and
// see exactly what the customer will see. Anything richer would need an editor,
// and an editor that produces markup is an editor that can produce markup the
// PDF cannot draw.
//
// ----------------------------------------------------------------------------
// The grammar, in full
// ----------------------------------------------------------------------------
//   ## Some heading        a heading
//   - a point              a bullet; consecutive lines make one list
//   anything else          a paragraph; blank lines separate them
//
// A line of "##" with nothing after it is a paragraph, not an empty heading.
// ============================================================================

const HEADING = /^##\s+(.*\S)\s*$/;
const BULLET = /^[-*]\s+(.*\S)\s*$/;

/**
 * @param {string} text  a body_snapshot, or a template body being previewed
 * @returns {Array<{type: "heading"|"paragraph"|"list", text?: string, items?: string[]}>}
 */
export function toBlocks(text) {
  const blocks = [];
  // \r\n first: a body pasted out of Word arrives with them, and splitting on
  // \n alone leaves a trailing \r on every line, which shows up as a stray
  // space in the PDF and nowhere in the browser.
  const lines = String(text || "").replace(/\r\n?/g, "\n").split("\n");

  let paragraph = [];
  let list = null;

  const flushParagraph = () => {
    if (!paragraph.length) return;
    blocks.push({ type: "paragraph", text: paragraph.join(" ") });
    paragraph = [];
  };

  const flushList = () => {
    if (!list) return;
    blocks.push({ type: "list", items: list });
    list = null;
  };

  for (const raw of lines) {
    const line = raw.trim();

    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ type: "heading", text: heading[1] });
      continue;
    }

    const bullet = BULLET.exec(line);
    if (bullet) {
      flushParagraph();
      if (!list) list = [];
      list.push(bullet[1]);
      continue;
    }

    // A wrapped line inside a bullet belongs to that bullet, not to a new
    // paragraph -- otherwise a long clause reflows into the body and loses its
    // marker. Anything not starting with a dash while a list is open continues
    // the last item.
    if (list) {
      list[list.length - 1] += ` ${line}`;
      continue;
    }

    paragraph.push(line);
  }

  flushParagraph();
  flushList();
  return blocks;
}

/**
 * Plain text with the layout preserved, for an email body or a copy-paste.
 * Not for display -- toBlocks is for that.
 */
export function toPlainText(text) {
  return toBlocks(text)
    .map((b) => {
      if (b.type === "heading") return `${b.text.toUpperCase()}\n`;
      if (b.type === "list") return b.items.map((i) => `  - ${i}`).join("\n");
      return b.text;
    })
    .join("\n\n");
}

/**
 * The first heading in a body, or null.
 * Used to warn an owner who has deleted every heading that their document will
 * render as one long wall of text.
 */
export function firstHeading(text) {
  const block = toBlocks(text).find((b) => b.type === "heading");
  return block ? block.text : null;
}

/** Rough reading length, for "about 4 pages" in the editor. */
export function wordCount(text) {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  return words.length;
}

export default { toBlocks, toPlainText, firstHeading, wordCount };
